import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Aurora Toolbar — responsive horizontal action cluster.
 *
 * Toolbars sit above (or beside) a content surface and host actions, filters,
 * and quick toggles. The shared primitive applies the standard rhythm so
 * every page doesn't reinvent the layout:
 *
 * - On `sm+` viewports a toolbar lays out as a single row, items separated
 *   by a 8px gap.
 * - On narrower viewports it stacks the leading/trailing groups vertically
 *   so primary actions don't get squeezed off-screen. `align="row"` forces
 *   the row layout (useful when the toolbar only carries icon buttons).
 *
 * The component is `role="toolbar"` by default with an `aria-orientation` of
 * `horizontal`; the consumer is responsible for `aria-label`-ing the toolbar.
 */
interface ToolbarProps extends React.ComponentProps<'div'> {
  /**
   * Layout strategy on narrow viewports.
   * - `stack` (default) — wraps onto a new line, with the trailing group
   *   pushed to the right via `flex-wrap`.
   * - `row` — keeps everything on a single row regardless of width. Useful
   *   for toolbars that only host icon buttons.
   */
  align?: 'stack' | 'row'
  /** Accessible label for the toolbar landmark. */
  'aria-label': string
}

function Toolbar({
  className,
  align = 'stack',
  children,
  ...props
}: ToolbarProps) {
  return (
    <div
      role="toolbar"
      aria-orientation="horizontal"
      data-slot="toolbar"
      data-align={align}
      className={cn(
        'flex w-full items-center gap-2',
        align === 'stack'
          ? 'flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:gap-2'
          : 'flex-row flex-nowrap',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/**
 * Action cluster — a sub-group of related controls inside a Toolbar. Use one
 * for the leading group, one for the trailing group; the trailing group will
 * push to the right on `sm+` viewports thanks to `sm:ml-auto`. Use
 * `position="trailing"` to mark the right-aligned cluster.
 *
 * Each cluster gets a thin internal gap so adjacent buttons read as a pair
 * (no extra spacing between consecutive icon buttons).
 */
interface ToolbarGroupProps extends React.ComponentProps<'div'> {
  /**
   * Whether this group hugs the left or right edge of the toolbar. Trailing
   * groups are flagged with `sm:ml-auto` so the toolbar can host an arbitrary
   * number of leading clusters.
   */
  position?: 'leading' | 'trailing'
}

function ToolbarGroup({
  className,
  position = 'leading',
  ...props
}: ToolbarGroupProps) {
  return (
    <div
      data-slot="toolbar-group"
      data-position={position}
      className={cn(
        'flex items-center gap-1.5',
        position === 'trailing' && 'sm:ml-auto',
        className,
      )}
      {...props}
    />
  )
}

/**
 * Hairline divider between clusters inside a Toolbar. Hidden on `xs` widths
 * because the toolbar wraps to a column there and a vertical separator would
 * read as a stray pixel.
 */
function ToolbarSeparator({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      data-slot="toolbar-separator"
      className={cn(
        'hidden h-5 w-px self-center bg-border-strong/60 sm:block',
        className,
      )}
      {...props}
    />
  )
}

export { Toolbar, ToolbarGroup, ToolbarSeparator }
